/**
 * Permission Modal - 위치정보 및 푸시 알림 권한 동의 모달
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { HiLocationMarker, HiBell, HiCheckCircle, HiInformationCircle } from 'react-icons/hi';

export default function PermissionModal({ onGrant }) {
  const [locationGranted, setLocationGranted] = useState(false);
  const [notificationGranted, setNotificationGranted] = useState(false);

  // 두 권한이 모두 허용되면 자동으로 진행
  useEffect(() => {
    if (locationGranted && notificationGranted) {
      const timer = setTimeout(() => {
        onGrant();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [locationGranted, notificationGranted, onGrant]);

  const handleLocationGrant = () => {
    setLocationGranted(true);
  };

  const handleNotificationGrant = () => {
    setNotificationGranted(true);
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden"
        style={{ background: 'var(--color-surface)' }}
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        {/* 헤더 */}
        <div
          className="px-6 py-4"
          style={{
            background: `linear-gradient(135deg, var(--color-primary), var(--color-secondary))`,
          }}
        >
          <h2 className="text-xl font-display font-bold text-white">
            권한 안내
          </h2>
          <p className="text-sm text-white/90 mt-1">
            서비스 이용을 위해 다음 권한이 필요합니다
          </p>
        </div>

        {/* 서비스 소개 */}
        <div className="px-6 pt-4 pb-3">
          <p className="text-sm leading-relaxed korean-text-relaxed font-medium" style={{ color: 'var(--color-text)' }}>
            제주도의 특별한 자기장 명소를 탐방하고, 스마트폰으로 자기장 수치를 직접 측정해보세요!
          </p>
        </div>

        {/* 권한 목록 */}
        <div className="p-6 space-y-4">
          {/* 위치 정보 권한 */}
          <motion.div
            className={`p-4 rounded-xl border-2 transition-all ${
              locationGranted ? 'opacity-60' : ''
            }`}
            style={{
              borderColor: locationGranted ? 'var(--color-primary)' : 'var(--color-primary)',
              background: locationGranted ? 'rgba(var(--color-primary-rgb), 0.05)' : 'transparent',
            }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <div
                  className="p-2 rounded-lg"
                  style={{ background: 'var(--color-primary)', color: 'white' }}
                >
                  <HiLocationMarker className="w-5 h-5" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-1" style={{ color: 'var(--color-text)' }}>
                  위치 정보 권한
                </h3>
                <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>
                  현재 위치를 기반으로 주변 자기장 스팟을 찾아드립니다.
                </p>
                {!locationGranted ? (
                  <motion.button
                    onClick={handleLocationGrant}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-white"
                    style={{
                      background: `linear-gradient(135deg, var(--color-primary), var(--color-secondary))`,
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    허용하기
                  </motion.button>
                ) : (
                  <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-primary)' }}>
                    <HiCheckCircle className="w-5 h-5" />
                    <span className="font-medium">허용됨</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* 푸시 알림 권한 */}
          <motion.div
            className={`p-4 rounded-xl border-2 transition-all ${
              notificationGranted ? 'opacity-60' : ''
            }`}
            style={{
              borderColor: notificationGranted ? 'var(--color-secondary)' : 'var(--color-secondary)',
              background: notificationGranted ? 'rgba(var(--color-secondary-rgb), 0.05)' : 'transparent',
            }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <div
                  className="p-2 rounded-lg"
                  style={{ background: 'var(--color-secondary)', color: 'white' }}
                >
                  <HiBell className="w-5 h-5" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-1" style={{ color: 'var(--color-text)' }}>
                  푸시 알림 권한
                </h3>
                <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>
                  스팟 존 진입 시 알림을 받을 수 있습니다.
                </p>
                {!notificationGranted ? (
                  <motion.button
                    onClick={handleNotificationGrant}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-white"
                    style={{
                      background: `linear-gradient(135deg, var(--color-secondary), var(--color-accent))`,
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    허용하기
                  </motion.button>
                ) : (
                  <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-secondary)' }}>
                    <HiCheckCircle className="w-5 h-5" />
                    <span className="font-medium">허용됨</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>

        {/* 주의사항 */}
        <div className="px-6 pb-4">
          <div className="flex items-start gap-3 p-4 rounded-lg border-2" style={{ background: 'var(--color-background)', borderColor: '#ef4444' }}>
            <HiInformationCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#ef4444' }} />
            <ul className="space-y-2 text-xs korean-text-relaxed" style={{ color: '#dc2626' }}>
              <li className="flex items-start">
                <span className="mr-2 flex-shrink-0 font-bold">•</span>
                <span className="flex-1 font-medium">본 앱은 의료 기기가 아니며, 어떠한 의학적 조언, 진단, 치료 정보도 제공하지 않습니다.</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 flex-shrink-0 font-bold">•</span>
                <span className="flex-1 font-medium">자기장 수치와 건강 사이의 관계에 대해 본 앱은 어떠한 주장도 하지 않습니다.</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 flex-shrink-0 font-bold">•</span>
                <span className="flex-1 font-medium">건강 관련 문의는 반드시 의료 전문가와 상담하시기 바랍니다.</span>
              </li>
            </ul>
          </div>
        </div>

        {/* 안내 문구 */}
        <div className="px-6 pb-6">
          <div className="flex items-start gap-2 p-3 rounded-lg" style={{ background: 'var(--color-background)' }}>
            <HiInformationCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-primary)' }} />
            <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              모든 권한을 허용하시면 서비스를 이용하실 수 있습니다.
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
